/**
 * Punti fedeltà guadagnati.
 *
 * Compila i riquadri [data-loyalty-points] (pagina prodotto, quick view,
 * carrello e cart drawer) con i punti che l'importo in data-price-cents fa
 * guadagnare: importo × tasso di accumulo della promozione attiva. Il tasso
 * viene chiesto all'intranet (endpoint /earn-rate) e tenuto in
 * sessionStorage per non ripetere la chiamata a ogni pagina; se l'API non
 * risponde, il piano fedeltà è spento o non c'è una promozione attiva, i
 * riquadri restano nascosti.
 *
 * Vive fuori dal contenuto di quick view, carrello e drawer perché gli
 * script iniettati lì dentro non vengono eseguiti (app.js ricarica solo i
 * file con src). Un MutationObserver riprende i riquadri ricreati a ogni
 * re-render via Section Rendering API (stesso schema di cart-loyalty.js).
 */
(function () {
  if (window.loyaltyPointsWidget) {
    return;
  }
  window.loyaltyPointsWidget = true;

  const CACHE_KEY = 'loyaltyEarnRate';
  const CACHE_TTL = 30 * 60 * 1000; // 30 minuti

  let rate = null;
  let pending = null;

  function readCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));

      if (!cached || Date.now() - cached.ts > CACHE_TTL) {
        return null;
      }

      return cached.rate;
    } catch (e) {
      return null;
    }
  }

  function writeCache(value) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ rate: value, ts: Date.now() }));
    } catch (e) {
      // Storage pieno o disabilitato: pazienza, si rifà la chiamata.
    }
  }

  // Risolve sempre: in caso di errore con un tasso nullo, che nasconde i
  // riquadri. "Nessuna promozione" viene cachato, l'errore di rete no.
  function fetchRate() {
    if (rate) {
      return Promise.resolve(rate);
    }

    const cached = readCache();
    if (cached) {
      rate = cached;
      return Promise.resolve(rate);
    }

    if (pending) {
      return pending;
    }

    const config = window.loyaltyConfig;
    if (!config) {
      return Promise.resolve({ points: 0, euro: 0 });
    }

    pending = fetch(`${config.apiUrl}/earn-rate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        api_key: config.apiToken,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        rate = data.success && data.data
          ? { points: Number(data.data.points) || 0, euro: Number(data.data.euro) || 0 }
          : { points: 0, euro: 0 };
        writeCache(rate);
        return rate;
      })
      .catch(() => {
        pending = null;
        return { points: 0, euro: 0 };
      });

    return pending;
  }

  // Stessa formula di OrderService::addPoints nell'intranet: il totale viene
  // troncato all'euro prima di applicare il tasso.
  function pointsFor(cents, earnRate) {
    if (!earnRate || earnRate.points <= 0 || earnRate.euro <= 0) {
      return 0;
    }

    return Math.round((Math.floor(cents / 100) / earnRate.euro) * earnRate.points);
  }

  function updateBox(box, earnRate) {
    const cents = parseInt(box.dataset.priceCents, 10) || 0;
    const points = pointsFor(cents, earnRate);

    if (points > 0) {
      box.querySelector('[data-loyalty-points-value]').textContent = points;
      box.style.display = '';
    } else {
      box.style.display = 'none';
    }
  }

  // Inizializza solo i riquadri nuovi: il flag evita che le nostre stesse
  // modifiche al DOM (osservate dal MutationObserver) rilancino il ricalcolo.
  function initAll() {
    const boxes = Array.from(document.querySelectorAll('[data-loyalty-points]'))
      .filter((box) => !box.__lpInit);

    // Niente riquadri, niente chiamata API (es. pagine collezione).
    if (!boxes.length) {
      return;
    }

    boxes.forEach((box) => {
      box.__lpInit = true;
    });

    fetchRate().then((earnRate) => {
      boxes.forEach((box) => updateBox(box, earnRate));
    });
  }

  function start() {
    initAll();

    // Qualunque flusso rigeneri quick view, carrello o drawer (apertura
    // prodotto, aggiunta al carrello, cambio quantità, sconti, riscatto)
    // produce riquadri nuovi: li riprendiamo da qui.
    new MutationObserver(initAll).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('product:variant-change', (e) => {
    if (!e.detail || !e.detail.variant) {
      return;
    }

    const info = document.getElementById(`ProductInfo-${e.detail.sectionId}`);
    const box = info && info.querySelector('[data-loyalty-points]');

    if (!box) {
      return;
    }

    box.dataset.priceCents = e.detail.variant.price;
    fetchRate().then((earnRate) => updateBox(box, earnRate));
  });
})();
